import { isEmpty } from "lodash";
import { useState } from "react";
import { createRecipient } from "src/api/contractSweepRecipientSetting";
import DialogInOne from "src/components/DialogInOne";
import { Button } from "src/components/MuiGenerals";
import YubiPrompter from "src/components/Prompter";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import {
  useAlerting,
  usePermission,
  useTranslation,
  useYubiPrompt,
} from "src/hooks";
import { queryClient } from "src/utils/config";
import { FeatureCodes } from "src/utils/constant";
import { appError } from "src/utils/errorHandler";
import { useZusDialogStore } from "src/zustand/store";
import { ViewGroupData } from "../../../types/ViewGroupData";
import AddRecipientDialogContent from "../components/AddRecipientDialogContent";
import {
  AddRecipientFields,
  initAddRecipientFields,
} from "../types/AddRecipientFields";

const AddPermission =
  FeatureCodes.assetManagement.ContractSweepRecipentSetting
    .ContractSweepRecipent.addRecord;

type Props = {
  viewGroupData: ViewGroupData;
};

const useAddRecipientDialog = ({ viewGroupData }: Props) => {
  const { translatePrefix } = usePageConfigProviderContext();
  const { getResAfterYubi, prompterConfig } = useYubiPrompt();
  const { hasPermission } = usePermission();
  const { t } = useTranslation(translatePrefix);
  const { alerting } = useAlerting();
  const zusDialog = useZusDialogStore();
  const [isOpen, setIsOpen] = useState(false);
  const [fields, setFields] = useState<AddRecipientFields>(
    initAddRecipientFields,
  );

  const { chainType, chainId, assetName, groupId } = viewGroupData;

  const onConfirm = async () => {
    zusDialog.openExtra("loadingDialog");
    const res = await getResAfterYubi(
      createRecipient,
      {
        groupId: groupId,
        chainType: chainType,
        chainId: chainId,
        assetName: assetName,
        riskType: fields.riskType,
        walletId: fields.rawWalletInfo?.id,
        status: 1,
        probability: fields.probability,
      },
      {
        isSkipErrorHandling: true,
      },
    );
    zusDialog.closeExtra();
    if (!res) return;

    if ((res as any).error?.code === -51) {
      return alerting("warning", t("duplicated_recipient_address"));
    }

    if (!res || (res as any).error) {
      return appError({
        ...(res as any).error,
        requestId: (res as any).requestId,
      });
    }

    if (res) {
      alerting("success", t("add_record_success"));
      queryClient.invalidateQueries({
        queryKey: ["get_recipient_group_view_group"],
      });
      onClose();
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setFields(initAddRecipientFields);
  };

  const isConfirmDisabled =
    isEmpty(fields.riskType) ||
    isEmpty(fields.rawWalletInfo?.id) ||
    isEmpty(fields.probability);

  const renderAddRecipientButtonAndDialog = () => {
    return (
      <>
        <YubiPrompter {...prompterConfig} />
        {hasPermission(AddPermission) && (
          <Button onClick={() => setIsOpen(true)} variant="outlined">
            {t("add_record")}
          </Button>
        )}
        <DialogInOne
          title={t("add_record")}
          self={{
            open: isOpen,
            onClose: onClose,
          }}
          content={
            <AddRecipientDialogContent
              viewGroupData={viewGroupData}
              fields={fields}
              setFields={setFields}
            />
          }
          isConfirmDisabled={isConfirmDisabled}
          onConfirm={onConfirm}
          onCancel={onClose}
        />
      </>
    );
  };

  return { renderAddRecipientButtonAndDialog };
};

export default useAddRecipientDialog;
